import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Users2, FileText, MessageSquare, PersonStanding, Stethoscope, Wand2, Activity, Menu, Sun, Moon, LogOut, Settings as SettingsIcon, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AdminAuthContext';
import { useTheme } from '../context/AdminThemeContext';
import logo from '../assets/adminlogo.png';
import './AdminLayout.css';

const NAV_LINKS = [
  { path: '/dashboard', label: 'Dashboard', Icon: LayoutGrid },
  { path: '/users', label: 'Users', Icon: Users2 },
  { path: '/modalities', label: 'Modalities', Icon: Stethoscope },
  { path: '/body-parts', label: 'Body Parts', Icon: PersonStanding },
  { path: '/techniques', label: 'Techniques', Icon: Wand2 },
  { path: '/reports', label: 'Reports', Icon: FileText },
  { path: '/reports/feedbacks', label: 'Feedbacks', Icon: MessageSquare },
  { path: '/activity-log', label: 'User Activity Log', Icon: Activity },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('admin_sidebar_collapsed') === 'true');
  const toggleSidebar = () => {
    setCollapsed(c => {
      localStorage.setItem('admin_sidebar_collapsed', String(!c));
      return !c;
    });
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className={`admin-layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <div className="admin-brand-icon"><img src={logo} alt="Umedex QC" className="admin-brand-logo" /></div>
          {!collapsed && (
            <div>
              <div className="admin-brand-main">UMEDEX QC</div>
              <div className="admin-brand-sub">Admin Panel</div>
            </div>
          )}
        </div>

        <nav className="admin-nav">
          {NAV_LINKS.map(({ path, label, Icon }) => (
            <Link
              key={path}
              to={path}
              title={collapsed ? label : undefined}
              className={`admin-nav-link ${path === '/reports'
                  ? (location.pathname === '/reports' || /^\/reports\/[^/]+$/.test(location.pathname)) && location.pathname !== '/reports/feedbacks'
                  : location.pathname.startsWith(path)
                    ? 'active' : ''}`}
            >
              <Icon size={18} className="admin-nav-icon" />
              {!collapsed && <span>{label}</span>}
            </Link>
          ))}
        </nav>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <button className="admin-sidebar-toggle" onClick={toggleSidebar} title="Toggle sidebar">
            <Menu size={19} />
          </button>

          <div className="admin-topbar-right">
            <button className="admin-topbar-icon-btn admin-theme-toggle" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} onClick={toggleTheme}>
              {isDark ? <Sun size={17} fill="currentColor" /> : <Moon size={17} fill="currentColor" />}
            </button>
            <div className="admin-profile-menu" ref={menuRef}>
              <button className="admin-profile" onClick={() => setMenuOpen(o => !o)}>
                <div className="admin-profile-av">{initials || '?'}</div>
                <div className="admin-profile-text">
                  <div className="admin-profile-name">{user?.first_name} {user?.last_name}</div>
                  <div className="admin-profile-role">{user?.role === 'superadmin' ? 'Super Admin' : 'Admin'}</div>
                </div>
                {/* <ChevronDown size={15} className={`admin-profile-chevron ${menuOpen ? 'open' : ''}`} /> */}
              </button>

              {menuOpen && (
                <div className="admin-profile-dropdown">
                  <Link to="/settings" className="admin-dropdown-item" onClick={() => setMenuOpen(false)}>
                    <SettingsIcon size={15} /> Settings
                  </Link>
                  <button
                    className="admin-dropdown-item danger"
                    onClick={() => { setMenuOpen(false); logout(); navigate('/login'); }}
                  >
                    <LogOut size={15} /> Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="admin-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;